import { useMemo, useState } from "react";
import { copy } from "../copy";
import Icon, { type IconName } from "../components/Icon";
import { api, type Inbox, type InboxKind } from "./api";

const KIND_META: Record<InboxKind, { icon: IconName; tone: string }> = {
  system: { icon: "megaphone", tone: "text-chrome-dim" },
  contest: { icon: "trophy", tone: "text-gold" },
  payout: { icon: "moneyDollar", tone: "text-money" },
  fan: { icon: "music", tone: "text-chrome" },
};

type Filter = "all" | "unread";

function when(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

// The inbox as the server sent it. Read state changes go through the API
// first; the list only updates once the server agreed.
export default function InboxTab({ inbox, onChange }: { inbox: Inbox; onChange: (inbox: Inbox) => void }) {
  const t = copy.dashboard.inbox;
  const [filter, setFilter] = useState<Filter>("all");
  const [selectedId, setSelectedId] = useState("");
  const [error, setError] = useState("");

  const visible = useMemo(
    () => (filter === "unread" ? inbox.messages.filter((m) => !m.is_read) : inbox.messages),
    [inbox, filter],
  );
  const selected = inbox.messages.find((m) => m.id === selectedId) ?? null;
  const unread = inbox.messages.filter((m) => !m.is_read).length;

  const setRead = (id: string, read: boolean) => {
    const messages = inbox.messages.map((m) => (m.id === id ? { ...m, is_read: read } : m));
    onChange({ messages, unread_count: messages.filter((m) => !m.is_read).length });
  };

  const open = async (id: string) => {
    setSelectedId(id);
    setError("");
    const msg = inbox.messages.find((m) => m.id === id);
    if (!msg || msg.is_read) return;
    const res = await api.setRead(id, true);
    if (res.ok) setRead(id, true);
    else setError(res.error);
  };

  const toggle = async (id: string, read: boolean) => {
    setError("");
    const res = await api.setRead(id, read);
    if (res.ok) setRead(id, read);
    else setError(res.error);
  };

  const markAll = async () => {
    setError("");
    const res = await api.readAll();
    if (res.ok) onChange({ messages: inbox.messages.map((m) => ({ ...m, is_read: true })), unread_count: 0 });
    else setError(res.error);
  };

  if (inbox.messages.length === 0) {
    return (
      <div data-testid="inbox-empty" className="flex flex-col items-center justify-center gap-2 rounded-2xl border border-ground-edge bg-ground-raised px-6 py-16 text-center">
        <Icon name="inbox" className="text-3xl text-chrome-dim" />
        <p className="text-sm text-chrome-dim">{t.empty}</p>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-2xl border border-ground-edge bg-ground-raised">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-ground-edge px-4 py-3.5 md:px-5">
        <div className="inline-flex rounded-full border border-ground-edge bg-ground p-1">
          {(["all", "unread"] as Filter[]).map((key) => (
            <button
              key={key}
              type="button"
              onClick={() => setFilter(key)}
              aria-pressed={filter === key}
              className={`bf-focusable cursor-pointer whitespace-nowrap rounded-full px-3.5 py-1.5 text-[11px] font-bold uppercase tracking-[0.14em] transition-colors ${
                filter === key ? "bg-gold text-ground" : "text-chrome-dim hover:text-chrome"
              }`}
            >
              {t[key]}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={markAll}
          disabled={unread === 0}
          className="bf-focusable flex cursor-pointer items-center gap-2 whitespace-nowrap text-[11px] font-bold uppercase tracking-[0.14em] text-money transition-colors hover:text-money-deep disabled:cursor-not-allowed disabled:text-chrome-dim"
        >
          <Icon name="mailOpen" className="text-base" />
          {t.markAll(unread)}
        </button>
      </div>

      {error && (
        <p role="alert" className="border-b border-ground-edge px-5 py-3 text-xs text-alert">
          {error}
        </p>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)]">
        <ul className="max-h-[560px] overflow-y-auto border-b border-ground-edge lg:border-b-0 lg:border-r">
          {visible.length === 0 ? (
            <li className="flex flex-col items-center justify-center gap-2 px-6 py-16 text-center">
              <Icon name="inbox" className="text-3xl text-chrome-dim" />
              <p className="text-sm text-chrome-dim">{t.emptyFilter}</p>
            </li>
          ) : (
            visible.map((m) => {
              const meta = KIND_META[m.kind];
              const active = selected?.id === m.id;
              return (
                <li key={m.id}>
                  <button
                    type="button"
                    data-testid="inbox-row"
                    onClick={() => open(m.id)}
                    className={`flex w-full cursor-pointer items-start gap-3 border-b border-ground-edge/60 px-4 py-4 text-left transition-colors md:px-5 ${
                      active ? "bg-ground" : "hover:bg-ground/60"
                    }`}
                  >
                    <span className="relative shrink-0">
                      <span className={`flex h-10 w-10 items-center justify-center rounded-full bg-ground text-lg ${meta.tone}`}>
                        <Icon name={meta.icon} />
                      </span>
                      {!m.is_read && (
                        <span className="absolute -right-0.5 -top-0.5 h-3 w-3 rounded-full border-2 border-ground-raised bg-money" />
                      )}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center justify-between gap-2">
                        <span className={`truncate text-sm ${m.is_read ? "font-medium text-chrome-dim" : "font-bold text-chrome"}`}>{m.from}</span>
                        <span className="shrink-0 text-[10px] uppercase tracking-[0.12em] text-chrome-dim">{when(m.created_at)}</span>
                      </span>
                      <span className={`mt-0.5 block truncate text-xs ${m.is_read ? "text-chrome-dim" : "text-chrome"}`}>{m.subject}</span>
                      <span className="mt-1 line-clamp-1 block text-[11px] text-chrome-dim">{m.preview}</span>
                    </span>
                  </button>
                </li>
              );
            })
          )}
        </ul>

        <div className="min-h-[320px] p-5 md:p-6">
          {!selected ? (
            <div className="flex h-full flex-col items-center justify-center gap-2 text-center">
              <Icon name="mailOpen" className="text-3xl text-chrome-dim" />
              <p className="text-sm text-chrome-dim">{t.pick}</p>
            </div>
          ) : (
            <article data-testid="inbox-message">
              <div className="flex items-start gap-3">
                <span className={`flex h-11 w-11 items-center justify-center rounded-full bg-ground text-lg ${KIND_META[selected.kind].tone}`}>
                  <Icon name={KIND_META[selected.kind].icon} />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-bold text-chrome">{selected.from}</p>
                  <p className="text-[11px] uppercase tracking-[0.14em] text-chrome-dim">
                    {t.kinds[selected.kind]} · {when(selected.created_at)}
                  </p>
                </div>
              </div>
              <h3 className="mt-5 font-headline text-2xl uppercase leading-tight text-chrome">{selected.subject}</h3>
              <p className="mt-4 whitespace-pre-line text-sm leading-relaxed text-chrome-dim">{selected.body}</p>
              <div className="mt-6 flex flex-wrap items-center gap-2">
                {selected.ctas.map((cta) => (
                  <a
                    key={cta.href}
                    href={cta.href}
                    className="bf-focusable flex h-10 items-center whitespace-nowrap rounded-md bg-money px-5 text-[11px] font-bold uppercase tracking-[0.14em] text-ground transition-colors hover:bg-money-deep"
                  >
                    {cta.label}
                  </a>
                ))}
                <button
                  type="button"
                  onClick={() => toggle(selected.id, !selected.is_read)}
                  className="bf-focusable h-10 cursor-pointer whitespace-nowrap rounded-md border border-ground-edge px-4 text-[11px] font-bold uppercase tracking-[0.14em] text-chrome transition-colors hover:border-chrome-dim/50"
                >
                  {selected.is_read ? t.markUnread : t.markRead}
                </button>
              </div>
            </article>
          )}
        </div>
      </div>
    </div>
  );
}
